import { useState } from 'react'
import { buttonClass } from './AuthLayout.tsx'

/**
 * The one moment recovery codes are readable (§6c).
 *
 * They are argon2id-hashed the instant they are made, exactly like a password,
 * so there is no "show them again" — reissuing is the only way to see a set,
 * and reissuing invalidates the old one. The screen says so plainly rather than
 * letting someone assume they can come back for them.
 */
export function RecoveryCodes({
  codes,
  onDone,
  doneLabel = 'I have saved these',
}: {
  codes: string[]
  onDone: () => void
  doneLabel?: string
}) {
  const [confirmed, setConfirmed] = useState(false)

  const asText = codes.join('\n')

  return (
    <div>
      <p className="text-sm text-ink">
        Keep these somewhere safe and offline. Each one signs you in once if you lose your
        authenticator or your key.
      </p>

      <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-line bg-surface-sunken p-3 font-mono text-sm">
        {codes.map((code) => (
          <li key={code} className="tabular-nums">
            {code}
          </li>
        ))}
      </ul>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => void navigator.clipboard.writeText(asText)}
          className="rounded-md border border-line px-3 py-1.5 text-xs font-semibold hover:border-brand-400"
        >
          Copy
        </button>
        <button
          type="button"
          onClick={() => {
            // A file, because the realistic place these end up is a printout or
            // a password manager, and re-typing ten codes off a screen is how
            // one of them ends up wrong.
            const url = URL.createObjectURL(new Blob([asText], { type: 'text/plain' }))
            const link = document.createElement('a')
            link.href = url
            link.download = 'blocwork-recovery-codes.txt'
            link.click()
            URL.revokeObjectURL(url)
          }}
          className="rounded-md border border-line px-3 py-1.5 text-xs font-semibold hover:border-brand-400"
        >
          Download
        </button>
      </div>

      <label className="mt-4 flex items-start gap-2 text-sm text-ink">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          I have saved these codes. I understand they will not be shown again — a new set can be
          generated, which cancels this one.
        </span>
      </label>

      <button type="button" disabled={!confirmed} onClick={onDone} className={`${buttonClass} mt-4`}>
        {doneLabel}
      </button>
    </div>
  )
}
